<template>
  <BaseSimpleLayout class="role-permission-edit-page">
    <template #title>ロール編集</template>
    <template #button-area>
      <el-button type="info" @click="goDetail">戻る</el-button>
      <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_EDIT">
        <el-button
          type="primary"
          :disabled="isLoading || isSaving || isSelfRole"
          @click="saveChanges"
        >
          更新
        </el-button>
      </ScreenPermissionGate>
    </template>

    <template #main>
      <div v-loading="isLoading" class="edit-form">
        <div class="edit-form__info">
          <el-form label-width="130px">
            <el-form-item required label="ロールコード">
              <el-input :model-value="roleDetail?.code ?? ''" disabled />
            </el-form-item>
            <el-form-item label="ロール名" required :error="roleNameError">
              <el-input
                v-model="roleName"
                maxlength="100"
                placeholder="例: 店舗管理者"
                :disabled="isSelfRole"
                @input="roleNameError = ''"
              />
            </el-form-item>
            <el-form-item
              label="ホーム画面"
              class="home-path-form-item"
              required
              :show-message="false"
              :error="homePathError"
            >
              <el-select
                v-model="homePath"
                class="home-path-select"
                :disabled="isSelfRole"
                @change="homePathError = ''"
              >
                <el-option
                  v-for="homePathOption in HOME_PATH_OPTIONS"
                  :key="homePathOption.path"
                  :label="homePathOption.label"
                  :value="homePathOption.path"
                />
              </el-select>
              <div
                v-if="homePathError"
                class="home-path-error-block"
                role="alert"
              >
                {{ homePathError }}
              </div>
            </el-form-item>
            <el-form-item label="ロール種別">
              <el-radio-group :model-value="roleDetail?.level ?? 1" disabled>
                <el-radio v-if="canSelectAdminRole" :label="2">管理者</el-radio>
                <el-radio :label="1">担当者</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="roleDetail?.level === 1" label="親ロール">
              <el-checkbox-group
                v-model="parentRoleCheckboxIds"
                class="managed-employee-checkbox-group"
                @change="onParentRoleCheckboxChange"
              >
                <el-checkbox
                  v-for="role in staffParentRoleOptions"
                  :key="role.id"
                  :label="role.id"
                  :disabled="role.level === 3"
                >
                  {{ role.name }}
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <el-form-item v-if="roleDetail?.level === 2" label="管理対象担当者">
              <el-checkbox-group
                v-model="managedChildRoleIds"
                class="managed-employee-checkbox-group"
              >
                <el-checkbox
                  v-for="role in managedEmployeeOptions"
                  :key="role.id"
                  :label="role.id"
                >
                  {{ role.name }}
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-form>
        </div>

        <div class="edit-form__table">
          <el-table
            :data="permissionRows"
            row-key="id"
            :tree-props="{ children: 'children' }"
            :cell-style="getCellStyle"
            border
            stripe
            :max-height="tableHeight"
            class="permission-table"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              fixed
              min-width="320"
            />
            <el-table-column
              label="Draft 仮登録"
              column-key="draft"
              width="190"
              align="center"
            >
              <template #default="scope">
                <RolePermissionMatrixCell
                  :row-kind="scope.row.kind"
                  :model-value="scope.row.draft"
                  :status="scope.row.draft"
                  :options="permissionOptions"
                  :changed="false"
                  :mode="isSelfRole ? 'view' : 'edit'"
                  @update:model-value="updatePermission(scope.row.id, $event)"
                />
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <BaseConfirmDialog
        v-model="updateConfirmVisible"
        title="更新の確認"
        message="Draft 仮登録 の変更を保存します。よろしいですか？"
        confirm-text="更新"
        cancel-text="キャンセル"
        confirm-type="primary"
        :loading="isSaving"
        @confirm="confirmSaveChanges"
      />
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import RolePermissionMatrixCell from '@/pages/master/role-permission/RolePermissionMatrixCell.vue';
  import { buildCellStyle } from '@/pages/master/role-permission/cellStyle';
  import {
    HOME_PATH_OPTIONS,
    resolveRoleHomePathForEdit,
    validateHomePathAgainstPermissions
  } from '@/pages/master/role-permission/homePathConfig';
  import {
    buildParentRoleCheckboxIdsFromDetail,
    buildParentRoleIdsForSave,
    buildStaffParentRoleOptions,
    normalizeParentRoleCheckboxIds
  } from '@/pages/master/role-permission/parentRoleHelpers';
  import router from '@/router';
  import store from '@/store';
  import type {
    ActionKey,
    RolePermissionDetail,
    RolePermissionRole,
    RolePermissionScreen
  } from '@/store/modules/rolePermission';

  type PermissionValue = 'full' | 'edit' | 'view' | 'limited' | 'none';
  type PermissionRow = {
    id: string;
    name: string;
    kind: 'screen' | 'button';
    draft: PermissionValue;
    originalDraft: PermissionValue;
    screenId?: number;
    children?: PermissionRow[];
  };

  const route = useRoute();
  const roleId = computed(() => Number(route.params.roleId ?? 0));
  const tableHeight = window.innerHeight - 240;
  const roleDetail = ref<RolePermissionDetail | null>(null);
  const roleName = ref('');
  const roleNameError = ref('');
  const managedChildRoleIds = ref<number[]>([]);
  const parentRoleCheckboxIds = ref<number[]>([]);
  const staffParentRoleOptions = ref<RolePermissionRole[]>([]);
  const allRoleDefinitions = ref<RolePermissionRole[]>([]);
  const managedEmployeeOptions = ref<RolePermissionRole[]>([]);
  const homePath = ref('');
  const homePathError = ref('');
  const screensCatalog = ref<RolePermissionScreen[]>([]);
  const permissionRows = ref<PermissionRow[]>([]);
  const changedCellMap = ref<Record<string, boolean>>({});
  const isLoading = ref(false);
  const isSaving = ref(false);
  const updateConfirmVisible = ref(false);
  const canSelectAdminRole = ref(false);
  const currentAuthRoleId = computed(() =>
    Number(store.getters['auth/auth'].role ?? 0)
  );
  const isSelfRole = computed(
    () => roleDetail.value?.id === currentAuthRoleId.value
  );

  /** Same as detail page: own role + managed children; else → /top. */
  const isRoleInCurrentUserScope = (
    allRoleDefinitions: RolePermissionRole[],
    targetRoleId: number
  ): boolean => {
    const loggedInUserRoleDefinition = allRoleDefinitions.find(
      (roleDefinition) => roleDefinition.id === currentAuthRoleId.value
    );
    if (!loggedInUserRoleDefinition) return false;
    return (
      loggedInUserRoleDefinition.id === targetRoleId ||
      (loggedInUserRoleDefinition.managedChildRoleIds ?? []).includes(
        targetRoleId
      )
    );
  };

  const permissionOptions = [
    { value: 'full', label: '登録/訂正/削除可' },
    { value: 'edit', label: '訂正可' },
    { value: 'view', label: '閲覧のみ' },
    { value: 'none', label: '権限無し' }
  ] as { value: PermissionValue; label: string }[];

  const actionKeyToPermission = (actionKey: ActionKey): PermissionValue => {
    if (actionKey === 'CRUD') return 'full';
    if (actionKey === 'EDIT') return 'edit';
    if (actionKey === 'VIEW') return 'view';
    return 'none';
  };
  const permissionToActionKey = (value: PermissionValue): ActionKey => {
    if (value === 'full') return 'CRUD';
    if (value === 'edit') return 'EDIT';
    if (value === 'view') return 'VIEW';
    return 'NONE';
  };
  const findRowById = (
    rows: PermissionRow[],
    id: string
  ): PermissionRow | null => {
    for (const row of rows) {
      if (row.id === id) return row;
      if (row.children?.length) {
        const child = findRowById(row.children, id);
        if (child) return child;
      }
    }
    return null;
  };
  const getCellKey = (rowId: string) => `${rowId}`;
  const updatePermission = (rowId: string, value: PermissionValue) => {
    if (isSelfRole.value) return;
    const row = findRowById(permissionRows.value, rowId);
    if (!row) return;
    row.draft = value;
    changedCellMap.value[getCellKey(rowId)] = row.originalDraft !== value;
  };
  const getCellStyle = buildCellStyle<PermissionRow>((row, column) => {
    if (column.columnKey !== 'draft') return null;
    return {
      highlight: changedCellMap.value[getCellKey(row.id)] === true,
      color: '#fff3bf'
    };
  });
  const collectPermissionDraft = (rows: PermissionRow[]) => {
    const result: { screenId: number; actionKey: ActionKey }[] = [];
    const walk = (nodes: PermissionRow[]) => {
      nodes.forEach((permissionRowNode) => {
        if (typeof permissionRowNode.screenId === 'number') {
          result.push({
            screenId: permissionRowNode.screenId,
            actionKey: permissionToActionKey(permissionRowNode.draft)
          });
        }
        if (permissionRowNode.children?.length) {
          walk(permissionRowNode.children);
        }
      });
    };
    walk(rows);
    return result;
  };
  const onParentRoleCheckboxChange = (values: number[]) => {
    parentRoleCheckboxIds.value = normalizeParentRoleCheckboxIds(
      values,
      staffParentRoleOptions.value
    );
  };

  const goDetail = () => {
    router.push({
      name: 'RolePermissionDetail',
      params: { roleId: String(route.params.roleId ?? '0') }
    });
  };
  const saveChanges = async () => {
    if (isSelfRole.value) {
      return;
    }
    roleNameError.value = '';
    homePathError.value = '';
    if (!roleName.value.trim()) {
      roleNameError.value = 'ロール名を入力してください。';
      return;
    }
    const homePathValidationMessage = validateHomePathAgainstPermissions(
      homePath.value,
      screensCatalog.value,
      permissionRows.value,
      'draft'
    );
    if (homePathValidationMessage) {
      homePathError.value = homePathValidationMessage;
      return;
    }
    updateConfirmVisible.value = true;
  };
  const confirmSaveChanges = async () => {
    const homePathValidationMessage = validateHomePathAgainstPermissions(
      homePath.value,
      screensCatalog.value,
      permissionRows.value,
      'draft'
    );
    if (homePathValidationMessage) {
      homePathError.value = homePathValidationMessage;
      updateConfirmVisible.value = false;
      return;
    }
    isSaving.value = true;
    try {
      await store.dispatch('rolePermission/updateRoleDraft', {
        roleId: roleId.value,
        body: {
          name: roleName.value.trim(),
          homePathDraft: homePath.value.trim() || null,
          managedChildRoleIds:
            roleDetail.value?.level === 2 ? managedChildRoleIds.value : [],
          parentRoleIds: buildParentRoleIdsForSave(
            roleDetail.value?.level ?? 1,
            parentRoleCheckboxIds.value,
            allRoleDefinitions.value
          ),
          permissionsDraft: collectPermissionDraft(permissionRows.value)
        }
      });
      updateConfirmVisible.value = false;
      goDetail();
    } finally {
      isSaving.value = false;
    }
  };

  onMounted(async () => {
    isLoading.value = true;
    try {
      const routeRoleId = roleId.value;
      if (!Number.isFinite(routeRoleId) || routeRoleId <= 0) {
        await router.replace('/top');
        return;
      }
      const [screens, roles] = await Promise.all([
        store.dispatch('rolePermission/fetchScreens'),
        store.dispatch('rolePermission/fetchRoles')
      ]);
      const rolePermissionScreens = screens as RolePermissionScreen[];
      allRoleDefinitions.value = roles as RolePermissionRole[];
      if (!isRoleInCurrentUserScope(allRoleDefinitions.value, routeRoleId)) {
        await router.replace('/top');
        return;
      }
      const detail = await store.dispatch(
        'rolePermission/fetchRoleDetail',
        routeRoleId
      );
      const fetchedRoleDetail = detail as RolePermissionDetail | null;
      if (!fetchedRoleDetail) {
        await router.replace('/top');
        return;
      }
      roleDetail.value = fetchedRoleDetail;
      if (isSelfRole.value) {
        goDetail();
        return;
      }
      roleName.value = fetchedRoleDetail.name ?? '';
      homePath.value = resolveRoleHomePathForEdit(
        fetchedRoleDetail.homePath,
        fetchedRoleDetail.homePathDraft
      );
      managedChildRoleIds.value = [
        ...(fetchedRoleDetail.managedChildRoleIds ?? [])
      ];
      staffParentRoleOptions.value = buildStaffParentRoleOptions(
        allRoleDefinitions.value
      );
      if (fetchedRoleDetail.level === 1) {
        parentRoleCheckboxIds.value = buildParentRoleCheckboxIdsFromDetail(
          fetchedRoleDetail,
          fetchedRoleDetail.id,
          allRoleDefinitions.value
        );
      } else {
        parentRoleCheckboxIds.value = [];
      }
      {
        const staffRolesAtLevelOne = allRoleDefinitions.value.filter(
          (roleDefinition) => roleDefinition.level === 1
        );
        const loggedInUserRoleDefinition = allRoleDefinitions.value.find(
          (roleDefinition) => roleDefinition.id === currentAuthRoleId.value
        );
        canSelectAdminRole.value =
          Number(loggedInUserRoleDefinition?.level) === 3;
        const existingManagedChildRoleIdsOnDetail =
          fetchedRoleDetail.managedChildRoleIds ?? [];
        if (!loggedInUserRoleDefinition) {
          managedEmployeeOptions.value = staffRolesAtLevelOne;
        } else {
          const managedChildRoleIdSetForLoggedInUser = new Set(
            loggedInUserRoleDefinition.managedChildRoleIds ?? []
          );
          const existingManagedChildRoleIdSet = new Set(
            existingManagedChildRoleIdsOnDetail
          );
          managedEmployeeOptions.value = staffRolesAtLevelOne.filter(
            (roleDefinition) =>
              managedChildRoleIdSetForLoggedInUser.has(roleDefinition.id) ||
              existingManagedChildRoleIdSet.has(roleDefinition.id)
          );
        }
      }
      screensCatalog.value = rolePermissionScreens;
      const draftPermissionByScreenId = (
        fetchedRoleDetail.permissionsDraft ?? []
      ).reduce(
        (
          permissionByScreenId: Record<number, PermissionValue>,
          permissionItem: { screenId: number; actionKey: ActionKey }
        ) => ({
          ...permissionByScreenId,
          [permissionItem.screenId]: actionKeyToPermission(
            permissionItem.actionKey
          )
        }),
        {}
      );
      permissionRows.value = rolePermissionScreens.map((screenRoot) => ({
        id: String(screenRoot.id),
        name: screenRoot.name,
        kind: 'screen',
        screenId: screenRoot.id,
        draft: draftPermissionByScreenId[screenRoot.id] ?? 'none',
        originalDraft: draftPermissionByScreenId[screenRoot.id] ?? 'none',
        children: screenRoot.childs.map((screenChild) => {
          const draftValue =
            draftPermissionByScreenId[screenChild.id] ?? 'none';
          return {
            id: String(screenChild.id),
            name: screenChild.name,
            kind: 'button',
            screenId: screenChild.id,
            draft: draftValue,
            originalDraft: draftValue
          };
        })
      }));
    } finally {
      isLoading.value = false;
    }
  });
</script>

<style lang="scss">
  .role-permission-edit-page {
    .edit-form {
      display: flex;
      gap: 10%;
      padding-top: 16px;

      &__info {
        flex: 3 1 0;
        min-width: 0;

        .el-input,
        .el-select {
          width: 100%;
        }

        :deep(.el-form-item__error) {
          padding-top: 4px;
          padding-left: 2px;
        }
      }

      &__table {
        flex: 7 1 0;
        min-width: 0;
      }
    }

    .managed-employee-checkbox-group {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .home-path-form-item {
      :deep(.el-form-item__content) {
        align-items: flex-start;
      }

      &.home-path-form-item--with-hint {
        :deep(.el-select .el-input__wrapper),
        :deep(.el-select__wrapper) {
          box-shadow: 0 0 0 1px var(--el-border-color, #dcdfe6) inset !important;
        }
        :deep(.el-select:hover:not(.is-disabled) .el-input__wrapper),
        :deep(.el-select:hover:not(.is-disabled) .el-select__wrapper) {
          box-shadow: 0 0 0 1px var(--el-border-color-hover, #c0c4cc) inset !important;
        }
        :deep(.el-select.is-focused .el-input__wrapper),
        :deep(.el-select.is-focused .el-select__wrapper) {
          box-shadow: 0 0 0 1px var(--el-color-primary, #409eff) inset !important;
        }
      }
    }

    .home-path-error-block {
      display: block;
      width: 100%;
      color: var(--el-color-danger, #f56c6c);
      font-size: 13px;
      line-height: 1.65;
      text-align: left;
      word-break: break-word;
    }
  }
</style>
